import { PaymentKeyHash, Script } from '@lucid-evolution/lucid';
import { OptionsGet, Token_With_Price_And_Date_And_Signature, createQueryURLString, isEqual, isNullOrBlank, optionsGetDefault, toJson } from '../../Commons';
import { FundEntity } from '../../Entities/Fund.Entity';
import { SmartDBApi } from '../index.exports';
import { fetchWrapper } from '../../lib/index.exports';

export class FundApi extends SmartDBApi {
    protected static _Entity = FundEntity;

    // #region api

    public static async isAdminApi(pkh: PaymentKeyHash): Promise<boolean> {
        try {
            if (isNullOrBlank(pkh)) {
                throw `pkh not defined`;
            }
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/is-admin/${pkh}`);
            if (response.status === 200) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - isAdminApi - pkh: ${pkh} - isAdmin: ${data.isAdmin} - response OK`);
                return data.isAdmin;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - isAdminApi - Error: ${error}`);
            throw error;
        }
    }

    public static async getByAdminApi<T extends FundEntity>(pkh: PaymentKeyHash, protocol_id?: string, optionsGet?: OptionsGet): Promise<T[]> {
        try {
            if (isNullOrBlank(pkh)) {
                throw `pkh not defined`;
            }
            //------------------
            const queryString = createQueryURLString({ protocol_id });
            //------------------
            let response;
            if (isEqual(optionsGet, optionsGetDefault)) {
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/by-admin/${pkh}${queryString}`);
            } else {
                const body = toJson(optionsGet);
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/by-admin/${pkh}${queryString}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body,
                });
            }
            //------------------
            if (response.status === 200) {
                const datas = await response.json();
                console.log(`[${this._Entity.className()}] - getByAdminApi - response OK`);
                const instances = datas.map((data: any) => this._Entity.fromPlainObject<T>(data));
                console.log(`[${this._Entity.className()}] - getByAdminApi - pkh: ${pkh} - len: ${instances.length}`);
                return instances;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getByAdminApi - Error: ${error}`);
            throw error;
        }
    }

    public static async getAllByProtocolApi<T extends FundEntity>(protocol_id: string, optionsGet?: OptionsGet): Promise<T[]> {
        try {
            //------------------
            const queryString = createQueryURLString({ protocol_id });
            //------------------
            let response;
            if (isEqual(optionsGet, optionsGetDefault)) {
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/all${queryString}`);
            } else {
                const body = toJson(optionsGet);
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/all${queryString}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body,
                });
            }
            //------------------
            if (response.status === 200) {
                const datas = await response.json();
                console.log(`[${this._Entity.className()}] - getAllByProtocolApi - response OK`);
                const instances = datas.map((data: any) => this._Entity.fromPlainObject<T>(data));
                console.log(`[${this._Entity.className()}] - getAllByProtocolApi - protocol_id: ${protocol_id} - len: ${datas.length}`);
                return instances;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getAllByProtocolApi - Error: ${error}`);
            throw error;
        }
    }

    public static async getAllByProtocolAndVersionApi<T extends FundEntity>(protocol_id: string, fundVersion?: number, optionsGet?: OptionsGet): Promise<T[]> {
        try {
            const queryString = createQueryURLString({ protocol_id, fundVersion });
            //------------------
            let response;
            if (isEqual(optionsGet, optionsGetDefault)) {
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/all${queryString}`);
            } else {
                const body = toJson(optionsGet);
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/all${queryString}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body,
                });
            }
            //------------------
            if (response.status === 200) {
                const datas = await response.json();
                console.log(`[${this._Entity.className()}] - getAllByProtocolAndVersionApi - response OK`);
                const instances = datas.map((data: any) => this._Entity.fromPlainObject<T>(data));
                console.log(`[${this._Entity.className()}] - getAllByProtocolAndVersionApi - protocol_id: ${protocol_id} - fundVersion: ${fundVersion} - len: ${datas.length}`);
                return instances;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getAllByProtocolAndVersionApi - Error: ${error}`);
            throw error;
        }
    }

    public static async getDeployedByProtocolApi<T extends FundEntity>(protocol_id: string, optionsGet?: OptionsGet): Promise<T[]> {
        try {
            const queryString = createQueryURLString({ protocol_id });
            //------------------
            let response;
            if (isEqual(optionsGet, optionsGetDefault)) {
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/deployed${queryString}`);
            } else {
                const body = toJson(optionsGet);
                response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/deployed${queryString}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body,
                });
            }
            //------------------
            if (response.status === 200) {
                const datas = await response.json();
                console.log(`[${this._Entity.className()}] - getDeployedByProtocolApi - response OK`);
                const instances = datas.map((data: any) => this._Entity.fromPlainObject<T>(data));
                console.log(`[${this._Entity.className()}] - getDeployedByProtocolApi - protocol_id: ${protocol_id} - len: ${datas.length}`);
                return instances;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getDeployedByProtocolApi - Error: ${error}`);
            throw error;
        }
    }

    public static async getByParamsByProtocolApi<T extends FundEntity>(protocol_id: string, paramsFilter?: Record<string, any>, optionsGet?: OptionsGet): Promise<T[]> {
        try {
            const queryString = createQueryURLString({ protocol_id });
            //------------------
            let response;
            const body = toJson({ paramsFilter, ...optionsGet });
            response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/by-params${queryString}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            //------------------
            if (response.status === 200) {
                const datas = await response.json();
                console.log(`[${this._Entity.className()}] - getByParamsByProtocolApi - response OK`);
                const instances = datas.map((data: any) => this._Entity.fromPlainObject<T>(data));
                console.log(`[${this._Entity.className()}] - getByParamsByProtocolApi - protocol_id: ${protocol_id} - len: ${datas.length}`);
                return instances;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getByParamsByProtocolApi - Error: ${error}`);
            throw error;
        }
    }

    public static async get_TokenFT_PriceADAx1e6_Api<T extends FundEntity>(
        instance: T,
        forceRefresh: boolean = false
        // forceRefreshSubTokensFT: boolean = false
    ): Promise<Token_With_Price_And_Date_And_Signature | undefined> {
        try {
            //------------------
            const queryString = createQueryURLString({ forceRefresh });
            //------------------
            const response = await fetchWrapper(
                `${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/FT_PriceADAx1e6-by-FundPolicy_CS/${instance.fdpFundPolicy_CS}${queryString}`
            );
            if (response.status === 200) {
                const data = await response.json();
                //----------------------------
                const token: Token_With_Price_And_Date_And_Signature = { ...data, priceADAx1e6: BigInt(data.priceADAx1e6), date: BigInt(data.date) };
                //----------------------------
                console.log(
                    `[${this._Entity.className()}] - get_TokenFT_PriceADAx1e6_Api - FundPolicy_CS: ${instance.fdpFundPolicy_CS} - FT PriceADAx1e6: ${
                        token.priceADAx1e6
                    } - response OK`
                );
                return token;
            } else if (response.status === 404) {
                console.log(`[${this._Entity.className()}] - get_TokenFT_PriceADAx1e6_Api - FundPolicy_CS: ${instance.fdpFundPolicy_CS} - FT PriceADAx1e6 not found`);
                return undefined;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - get_TokenFT_PriceADAx1e6_Api - Error: ${error}`);
            throw error;
        }
    }

    public static async getTotal_FT_MintedApi<T extends FundEntity>(instance: T): Promise<bigint> {
        try {
            const response = await fetchWrapper(
                `${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/total_FT_Minted-by-FundPolicy_CS/${instance.fdpFundPolicy_CS}`
            );
            if (response.status === 200) {
                const data = await response.json();
                console.log(
                    `[${this._Entity.className()}] - getTotal_FT_MintedApi - FundPolicy_CS: ${instance.fdpFundPolicy_CS} - Total FT Minted: ${data.total_FT_Minted} - response OK`
                );
                return BigInt(data.total_FT_Minted);
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - getTotal_FT_MintedApi - Error: ${error}`);
            throw error;
        }
    }

    public static async get_TVLx1e6_Api<T extends FundEntity>(instance: T): Promise<bigint | undefined> {
        try {
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/TVLx1e6-by-FundPolicy_CS/${instance.fdpFundPolicy_CS}`);
            if (response.status === 200) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - get_TVLx1e6_Api - FundPolicy_CS: ${instance.fdpFundPolicy_CS} - TVLx1e6: ${data.TVLx1e6} - response OK`);
                return BigInt(data.TVLx1e6);
            } else if (response.status === 404) {
                console.log(`[${this._Entity.className()}] - get_TVLx1e6_Api - FundPolicy_CS: ${instance.fdpFundPolicy_CS} - TVLx1e6 not found`);
                return undefined;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - get_TVLx1e6_Api - Error: ${error}`);
            throw error;
        }
    }

    public static async optimizeValidatorApi(scriptSerialized: string, params: any): Promise<Script | undefined> {
        try {
            let response;
            //------------------
            const paramsWithType = Object.entries(params).map(([key, value]) => `${typeof value}:${value}`);
            //------------------
            const body = toJson({ scriptSerialized, paramsWithType });
            //------------------
            response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/optimize-validator`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            //------------------
            if (response.status === 200) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - optimizeValidatorApi - response OK`);
                return data.script;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - optimizeValidatorApi - Error: ${error}`);
            throw error;
        }
    }

    public static async optimizeMintingPolicyApi(scriptSerialized: string, params: any): Promise<Script | undefined> {
        try {
            let response;
            //------------------
            const paramsWithType = Object.entries(params).map(([key, value]) => `${typeof value}:${value}`);
            //------------------
            const body = toJson({ scriptSerialized, paramsWithType });
            //------------------
            response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/optimize-policy`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            //------------------
            if (response.status === 200) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - optimizeMintingPolicyApi - response OK`);
                return data.script;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - optimizeMintingPolicyApi - Error: ${error}`);
            throw error;
        }
    }

    public static async deployScriptApi(contract: string, params: any): Promise<Script | undefined> {
        try {
            let response;
            //------------------
            const paramsWithType = Object.entries(params).map(([key, value]) => `${typeof value}:${value}`);
            //------------------
            const body = toJson({ contract, paramsWithType });
            //------------------
            response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${this._Entity.apiRoute()}/deploy-script`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body,
            });
            //------------------
            if (response.status === 200) {
                const data = await response.json();
                console.log(`[${this._Entity.className()}] - deployScriptApi - response OK`);
                return data.script;
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - deployScriptApi - Error: ${error}`);
            throw error;
        }
    }

    // #endregion api
}
